<template>
  <div :class="$style.fieldGroup">
    <span :class="$style.fieldLabel">{{
      t('regionalHighlight.purchaseModal.bookingPeriodLabel')
    }}</span>
    <HgSegmentedButton
      v-model="durationSegment"
      name="regional-highlight-purchase-duration"
      :options="durationOptions"
      data-testid="regional-highlight-purchase-duration"
    />
    <div
      :class="$style.startDateWrapper"
      @click="openContractStartMonthPicker"
      @keydown="onContractStartMonthPickerKeydown"
    >
      <HgInput
        :modelValue="contractStartMonth"
        type="month"
        name="regional-highlight-purchase-contract-start"
        :label="t('regionalHighlight.purchaseModal.contractStartDate')"
        :min="minContractStartMonth"
        :max="maxContractStartMonth"
        data-testid="regional-highlight-purchase-contract-start"
        @update:modelValue="onContractStartMonthChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { RegionalHighlightPurchaseDurationMonths } from '@/types/regionalHighlight';
import { HgInput, HgSegmentedButton } from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { clampContractStartDate } from './regionalHighlightPurchaseSummary';

const DURATION_VALUES = new Set<RegionalHighlightPurchaseDurationMonths>([1, 3, 6, 12]);

const durationMonths = defineModel<RegionalHighlightPurchaseDurationMonths>('durationMonths', {
  required: true,
});
const contractStartDate = defineModel<string>('contractStartDate', { required: true });

const props = defineProps<{
  durationOptions: Array<{ value: string; text: string; disabled?: boolean }>;
  minContractStartDate: string;
  maxContractStartDate: string;
}>();

const { t } = useI18n();

const minContractStartMonth = computed(() => props.minContractStartDate.slice(0, 7));
const maxContractStartMonth = computed(() => props.maxContractStartDate.slice(0, 7));

const contractStartMonth = computed(() => contractStartDate.value.slice(0, 7));

const onContractStartMonthChange = (value: string | number): void => {
  const yearMonth = String(value);
  if (!yearMonth) {
    return;
  }
  contractStartDate.value = clampContractStartDate(
    `${yearMonth}-01`,
    props.minContractStartDate,
    props.maxContractStartDate,
  );
};

const durationSegment = computed({
  get: () => String(durationMonths.value),
  set: (value: string) => {
    const months = Number(value) as RegionalHighlightPurchaseDurationMonths;
    if (!DURATION_VALUES.has(months)) {
      return;
    }
    durationMonths.value = months;
  },
});

const showContractStartMonthPicker = (container: HTMLElement): void => {
  const input = container.querySelector<HTMLInputElement>('input');
  if (input && !input.disabled) {
    input.showPicker?.();
  }
};

const openContractStartMonthPicker = (event: MouseEvent): void => {
  const target = event.target as HTMLElement | null;
  if (target?.closest('input')) {
    return;
  }

  showContractStartMonthPicker(event.currentTarget as HTMLElement);
};

const onContractStartMonthPickerKeydown = (event: KeyboardEvent): void => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  showContractStartMonthPicker(event.currentTarget as HTMLElement);
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.fieldGroup {
  display: flex;
  flex-direction: column;
  gap: $space-50;
}

.fieldLabel {
  @include body-2;

  color: $color-deep-blue-100;
}

.startDateWrapper {
  cursor: pointer;
}
</style>
